import {
  and,
  assign,
  emit,
  enqueueActions,
  fromCallback,
  fromPromise,
  not,
  setup,
  type ActorRefFrom,
  type SnapshotFrom,
} from "xstate";
import isEqual from "fast-deep-equal";

import { rebaseScratchpad, type ScratchpadChoice, type ScratchpadConflict } from "./merge";
import type { ScratchpadDocument, ScratchpadSource } from "./document";
import { indexScratchpadAuthors, type ScratchpadAuthorIndex } from "./authors";

type Document = ScratchpadDocument | null;

export type ScratchpadSnapshot = { document: ScratchpadDocument; concurrency: string; updated: string } | null;

export interface ScratchpadWriteInput {
  document: ScratchpadDocument | null;
  acknowledged: ScratchpadSnapshot;
}

export type ScratchpadWriteResult =
  | { type: "saved"; acknowledged: ScratchpadSnapshot }
  | { type: "conflict" }
  | { type: "error" };

export interface ScratchpadEntry {
  value: ScratchpadDocument;
  created: string;
  updated: string | null;
  tenant: string;
  product: string;
  identityProviderId: string;
  concurrency: string;
  reference: string;
}

export interface ScratchpadPersistence<Location> {
  // The owner's scratchpad, or the one with `reference`. Rejects with a 404 when it doesn't exist.
  get(location: Location, reference?: string): Promise<ScratchpadEntry>;
  // Every saved scratchpad for the case, including the owner's, without its content.
  getSiblings(location: Location): Promise<Array<Omit<ScratchpadEntry, "value">>>;
  save(
    location: Location,
    value: ScratchpadDocument,
    concurrency: string | null,
  ): Promise<Pick<ScratchpadEntry, "concurrency" | "created" | "updated">>;
  remove(location: Location, concurrency: string): Promise<void>;
  getErrorStatus(error: unknown): number | undefined;
}

// A failed refresh keeps the last loaded index; "failed" means nothing has loaded yet.
export type ScratchpadAuthorsState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "loaded"; index: ScratchpadAuthorIndex };

export const SCRATCHPAD_AUTHORS_POLL_INTERVAL = 30_000;

// Loads the owner's scratchpad, or another author's by `reference`. A 404 means there is none: the owner hasn't
// saved one, or the scratchpad was cleared, including an author's since it was listed.
export async function loadScratchpad<Location>(
  persistence: ScratchpadPersistence<Location>,
  location: Location,
  reference?: string,
): Promise<ScratchpadSnapshot> {
  try {
    const entry = await (reference == null ? persistence.get(location) : persistence.get(location, reference));
    return { document: entry.value, concurrency: entry.concurrency, updated: entry.updated ?? entry.created };
  } catch (error) {
    if (persistence.getErrorStatus(error) === 404) return null;
    throw error;
  }
}

// identityProviderId is case-insensitive; lowercase it once so authors are compared and keyed as-is.
// Mapping inside the fetch means any failure rejects into the `authors` region's onError, never the owner's autosave.
export async function loadScratchpadAuthors<Location>(
  persistence: ScratchpadPersistence<Location>,
  location: Location,
): Promise<Array<Omit<ScratchpadEntry, "value">>> {
  const siblings = await persistence.getSiblings(location);
  return siblings.map((sibling) => ({ ...sibling, identityProviderId: sibling.identityProviderId.toLowerCase() }));
}

export async function writeScratchpad<Location>(
  persistence: ScratchpadPersistence<Location>,
  location: Location,
  { document, acknowledged }: ScratchpadWriteInput,
): Promise<ScratchpadWriteResult> {
  try {
    if (document === (acknowledged?.document ?? null)) return { type: "saved", acknowledged };
    if (document != null) {
      const saved = await persistence.save(location, document, acknowledged?.concurrency ?? null);
      return {
        type: "saved",
        acknowledged: { document, concurrency: saved.concurrency, updated: saved.updated ?? saved.created },
      };
    }
    if (acknowledged != null) {
      await persistence.remove(location, acknowledged.concurrency).catch((error: unknown) => {
        if (persistence.getErrorStatus(error) !== 404) throw error;
      });
    }
    return { type: "saved", acknowledged: null };
  } catch (error) {
    if (persistence.getErrorStatus(error) === 409) return { type: "conflict" };
    console.error("Unable to save scratchpad", error);
    return { type: "error" };
  }
}

type Choose = (conflict: ScratchpadConflict) => ScratchpadChoice | undefined;
type Context = {
  ownerId: string;
  collapsed: boolean;
  // The owner's scratchpad (`owner` region).
  document: Document;
  acknowledged: ScratchpadSnapshot;
  // Set while `conflict` is active; the first diverging acknowledgment is the base for the whole episode.
  conflict: { documentBase: Document } | null;
  // A save or clear failed. It's shown, and nothing autosaves (not even on FLUSH) until the next edit clears it.
  writeFailed: boolean;
  refreshError: unknown;
  // A FLUSH during a request: once it settles, remaining edits save immediately instead of waiting for the window.
  flushRequested: boolean;
  latestAddedDataPointSource: ScratchpadSource | null;
  // Other authors with a scratchpad (`authors` region).
  authors: ScratchpadAuthorsState;
  // The other author shown instead of the owner, kept while collapsed; null shows the owner's (`viewing` region).
  // `snapshot` is null until it loads, and once it fails to load or is cleared. `fetched` is the listed concurrency
  // last fetched, so each listed version is fetched once, even when the fetch returns another version or a 404.
  viewing: { identityProviderId: string; snapshot: ScratchpadSnapshot; fetched: string | null } | null;
};
type Event =
  | { type: "UPDATE"; document: Document }
  | { type: "CAPTURED"; source: ScratchpadSource }
  | { type: "FLUSH" | "FOCUS" | "RETRY" | "STOP" }
  | { type: "SYNC"; choose?: Choose }
  | { type: "PANEL.EXPAND" | "PANEL.COLLAPSE" | "PANEL.TOGGLE" }
  | { type: "AUTHORS.DUE" }
  | { type: "AUTHOR.SELECT"; identityProviderId: string }
  | { type: "AUTHOR.RETRY" };

// The conflict episode's three-way merge is computed on demand and never stored.
function reconcile(context: Context, choose?: Choose) {
  const theirs = context.acknowledged?.document ?? null;
  const result = rebaseScratchpad(context.conflict!.documentBase, context.document, theirs, choose);
  // A merge equal to the server document reuses its reference, so the write is skipped.
  return result.resolved && isEqual(result.document, theirs) ? { ...result, document: theirs } : result;
}

function unchanged(context: Context, remote: ScratchpadSnapshot) {
  return (remote?.concurrency ?? null) === (context.acknowledged?.concurrency ?? null);
}

// The viewed author's current listing, once the author list has loaded.
function listedAuthor(context: Context) {
  return context.viewing != null && context.authors.status === "loaded"
    ? context.authors.index.get(context.viewing.identityProviderId)
    : undefined;
}

// Continuous typing saves at most once per window, plus request time; FLUSH bypasses it.
export const SAVE_WINDOW = 300;

export const scratchpadMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as { ownerId: string },
    tags: {} as "busy" | "readonly" | "review",
    emitted: {} as { type: "documentReplaced"; document: Document },
  },
  actors: {
    load: fromPromise<ScratchpadSnapshot>(() => Promise.reject(new Error("Scratchpad load actor must be provided"))),
    write: fromPromise<ScratchpadWriteResult, ScratchpadWriteInput>(() =>
      Promise.reject(new Error("Scratchpad write actor must be provided")),
    ),
    loadAuthors: fromPromise<Array<Omit<ScratchpadEntry, "value">>>(() =>
      Promise.reject(new Error("Scratchpad loadAuthors actor must be provided")),
    ),
    loadAuthor: fromPromise<ScratchpadSnapshot, { reference: string }>(() =>
      Promise.reject(new Error("Scratchpad loadAuthor actor must be provided")),
    ),
    // Animation frames don't run in hidden tabs, so a due poll waits until the tab is visible again.
    waitForPoll: fromCallback<Event>(({ sendBack }) => {
      let frame: number | undefined;
      const timer = setTimeout(
        () => (frame = requestAnimationFrame(() => sendBack({ type: "AUTHORS.DUE" }))),
        SCRATCHPAD_AUTHORS_POLL_INTERVAL,
      );
      return () => {
        clearTimeout(timer);
        if (frame != null) cancelAnimationFrame(frame);
      };
    }),
  },
  delays: { saveWindow: SAVE_WINDOW },
  guards: {
    dirty: ({ context }) => context.document !== (context.acknowledged?.document ?? null),
    resolvable: ({ context }) => reconcile(context).resolved,
    collapsed: ({ context }) => context.collapsed,
    autosave: ({ context }) => !context.writeFailed,
  },
  actions: {
    // With neither content nor a case summary there's nothing to save, so the scratchpad is cleared. Only an explicit
    // clear (a null document) replaces the editor; one emptied by editing keeps its editor and undo history.
    update: enqueueActions(({ event, enqueue }) => {
      if (event.type !== "UPDATE") return;

      enqueue.assign({
        writeFailed: false,
        document:
          event.document == null ||
          (event.document.caseSummary === "" &&
            event.document.content.content?.some(
              (node) => node.type === "dataPoint" || (node.content?.length ?? 0) > 0,
            ) !== true)
            ? null
            : event.document,
      });
      
      if (event.document == null) enqueue.emit({ type: "documentReplaced", document: null });
    }),
    // A clean document follows the server; local edits are never overwritten.
    acknowledge: assign(({ context }, { acknowledged }: { acknowledged: ScratchpadSnapshot }) => ({
      acknowledged,
      document:
        context.document === (context.acknowledged?.document ?? null)
          ? (acknowledged?.document ?? null)
          : context.document,
      refreshError: null,
    })),
    accept: enqueueActions(({ context, enqueue }, result: ScratchpadWriteResult) => {
      if (result.type !== "saved") return;
      // Accepting a reconciled document ends the episode and reloads the editor with what was sent.
      if (context.conflict != null) {
        const document = result.acknowledged?.document ?? null;
        enqueue.assign({ document });
        enqueue.emit({ type: "documentReplaced", document });
      }
      enqueue.assign({
        acknowledged: result.acknowledged,
        conflict: null,
        writeFailed: false,
        refreshError: null,
      });
    }),
    // The first diverging acknowledgment is the base for the whole episode, through repeated 409s.
    startConflict: assign(({ context }) => ({
      conflict: context.conflict ?? { documentBase: context.acknowledged?.document ?? null },
      writeFailed: false,
      refreshError: null,
    })),
    failRefresh: assign((_, { error }: { error: unknown }) => {
      console.error("Unable to refresh scratchpad", error);
      return { refreshError: error };
    }),
    // `writeScratchpad` reports failures as results; this covers a provided `write` actor that rejects instead.
    failWrite: assign((_, { error }: { error: unknown }) => {
      console.error("Unable to save scratchpad", error);
      return { writeFailed: true };
    }),
    clearErrors: assign({ writeFailed: false, refreshError: null }),
  },
}).createMachine({
  id: "scratchpad",
  type: "parallel",
  context: ({ input }) => ({
    ownerId: input.ownerId.toLowerCase(),
    collapsed: true,
    document: null,
    acknowledged: null,
    conflict: null,
    writeFailed: false,
    refreshError: null,
    flushRequested: false,
    latestAddedDataPointSource: null,
    authors: { status: "loading" },
    viewing: null,
  }),
  // Leaving any state cancels its delayed transitions and invoked waits; there are no timers to clean up.
  on: {
    STOP: {
      target: [".owner.inactive", ".authors.inactive", ".viewing.inactive"],
      // Ends any conflict episode too, so `context.conflict` is only ever set while in `conflict`.
      actions: assign({ collapsed: true, viewing: null, conflict: null }),
    },
    "PANEL.EXPAND": { actions: assign({ collapsed: false }) },
    "PANEL.COLLAPSE": { actions: assign({ collapsed: true }) },
    "PANEL.TOGGLE": { actions: assign({ collapsed: ({ context }) => !context.collapsed }) },
    CAPTURED: { actions: assign({ latestAddedDataPointSource: ({ event }) => event.source }) },
  },
  states: {
    // The current user's own scratchpad, autosaved through the load and write actors.
    owner: {
      initial: "loading",
      states: {
        loading: {
          tags: ["busy", "readonly"],
          invoke: {
            src: "load",
            onDone: {
              target: "editing",
              actions: { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
            },
            onError: {
              target: "loadFailed",
              actions: ({ event }) => console.error("Unable to load scratchpad", event.error),
            },
          },
        },
        loadFailed: { tags: "readonly", on: { RETRY: { target: "loading" } } },
        // At rest the document is acknowledged; any pending edit opens a save window, or saves now after a FLUSH.
        // After a failed save, pending edits wait here, unsent, until the next edit resumes autosave.
        editing: {
          always: [
            { guard: and(["dirty", "autosave", ({ context }) => context.flushRequested]), target: "saving" },
            { guard: and(["dirty", "autosave"]), target: "scheduled" },
          ],
          on: {
            UPDATE: { target: "scheduled", actions: { type: "update" } },
            SYNC: { target: "refreshing" },
            FOCUS: { target: "refreshing" },
          },
        },
        scheduled: {
          // Throttle: the window opens on the first edit and later edits do not postpone it.
          after: { saveWindow: { target: "saving" } },
          on: {
            UPDATE: { actions: { type: "update" } },
            FLUSH: { target: "saving" },
            SYNC: { target: "refreshing" },
            FOCUS: { target: "refreshing" },
          },
        },
        // One request is in flight at a time; edits and flushes are accepted until it settles.
        saving: {
          tags: "busy",
          entry: assign({ flushRequested: false }),
          on: {
            UPDATE: { actions: { type: "update" } },
            FLUSH: { actions: assign({ flushRequested: true }) },
          },
          invoke: {
            src: "write",
            input: ({ context }) => ({ document: context.document, acknowledged: context.acknowledged }),
            onDone: [
              {
                // Edits received during the request stay dirty and reopen a save window from editing.
                guard: ({ event }) => event.output.type === "saved",
                target: "editing",
                actions: { type: "accept", params: ({ event }) => event.output },
              },
              {
                guard: ({ event }) => event.output.type === "conflict",
                target: "conflict",
                actions: { type: "startConflict" },
              },
              // Stop automatic saving on ordinary failure; the local document is retained.
              { target: "editing", actions: assign({ writeFailed: true, flushRequested: false }) },
            ],
            onError: {
              target: "editing",
              actions: [
                { type: "failWrite", params: ({ event }) => ({ error: event.error }) },
                assign({ flushRequested: false }),
              ],
            },
          },
        },
        // Background GETs are serialized with writes while still accepting edits and flushes.
        refreshing: {
          tags: "busy",
          entry: assign({ flushRequested: false }),
          on: {
            UPDATE: { actions: { type: "update" } },
            // A FLUSH while autosave is paused doesn't count, even if an edit then resumes it.
            FLUSH: { guard: "autosave", actions: assign({ flushRequested: true }) },
          },
          invoke: {
            src: "load",
            onDone: [
              {
                // An unchanged server version needs no comparison; pending edits reopen a save window.
                guard: ({ context, event }) => unchanged(context, event.output),
                target: "editing",
                actions: { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
              },
              {
                // With no local edits, a newer server version simply replaces the document.
                guard: not("dirty"),
                target: "editing",
                actions: [
                  { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
                  emit(({ context }) => ({ type: "documentReplaced", document: context.document })),
                ],
              },
              {
                // A newer server version with local edits starts an episode based on the previous acknowledgment.
                target: "conflict.merging",
                actions: [
                  { type: "startConflict" },
                  { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
                ],
              },
            ],
            // A background refresh failure keeps the acknowledgment and any pending edits.
            onError: {
              target: "editing",
              actions: { type: "failRefresh", params: ({ event }) => ({ error: event.error }) },
            },
          },
        },
        // A conflict episode: `context.conflict` is set throughout, and only an accepted write ends it.
        conflict: {
          initial: "recovering",
          states: {
            recovering: {
              tags: "busy",
              // After a 409, edits remain local and cannot schedule another save until compared.
              on: { UPDATE: { actions: { type: "update" } } },
              invoke: {
                src: "load",
                onDone: {
                  target: "merging",
                  actions: { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
                },
                onError: {
                  // Keep the base and local draft so Review can retry the failed fetch.
                  target: "pending",
                  actions: { type: "failRefresh", params: ({ event }) => ({ error: event.error }) },
                },
              },
            },
            // A conflict-free merge saves automatically; otherwise the user must review.
            merging: { always: [{ guard: "resolvable", target: "saving" }, { target: "pending" }] },
            // The merge save freezes the editor; it reloads with the accepted document.
            saving: {
              tags: ["busy", "readonly"],
              invoke: {
                src: "write",
                input: ({ context }) => ({ document: reconcile(context).document, acknowledged: context.acknowledged }),
                onDone: [
                  {
                    guard: ({ event }) => event.output.type === "saved",
                    target: "#scratchpad.owner.editing",
                    actions: { type: "accept", params: ({ event }) => event.output },
                  },
                  {
                    guard: ({ event }) => event.output.type === "conflict",
                    target: "recovering",
                    actions: { type: "clearErrors" },
                  },
                  { target: "pending", actions: assign({ writeFailed: true }) },
                ],
                onError: {
                  target: "pending",
                  actions: { type: "failWrite", params: ({ event }) => ({ error: event.error }) },
                },
              },
            },
            // Before Review, editing is allowed but saves and focus refreshes remain paused.
            pending: {
              on: {
                UPDATE: { actions: { type: "update" } },
                SYNC: { target: "review" },
              },
            },
            review: {
              tags: ["readonly", "review"],
              initial: "refreshing",
              states: {
                // Review entry and repeated 409s both fetch the latest server version with editing frozen.
                refreshing: {
                  tags: "busy",
                  entry: { type: "clearErrors" },
                  invoke: {
                    src: "load",
                    onDone: {
                      // The fetched token keys the choices component, resetting choices for a new server version.
                      target: "comparing",
                      actions: { type: "acknowledge", params: ({ event }) => ({ acknowledged: event.output }) },
                    },
                    onError: {
                      // A failed review refresh must not expose stale choices for submission or unlock editing.
                      target: "failed",
                      actions: { type: "failRefresh", params: ({ event }) => ({ error: event.error }) },
                    },
                  },
                },
                // No choices needed: submit without leaving the readonly review lifecycle.
                comparing: { always: [{ guard: "resolvable", target: "submitting" }, { target: "choosing" }] },
                choosing: {
                  on: {
                    // Continue submits only a complete choice set; the write computes the merge from SYNC.
                    SYNC: {
                      guard: ({ context, event }) => reconcile(context, event.choose).resolved,
                      target: "submitting",
                      actions: { type: "clearErrors" },
                    },
                  },
                },
                submitting: {
                  tags: "busy",
                  invoke: {
                    src: "write",
                    input: ({ context, event }) => ({
                      document: reconcile(context, event.type === "SYNC" ? event.choose : undefined).document,
                      acknowledged: context.acknowledged,
                    }),
                    onDone: [
                      {
                        guard: ({ event }) => event.output.type === "saved",
                        target: "#scratchpad.owner.editing",
                        actions: { type: "accept", params: ({ event }) => event.output },
                      },
                      // A rejected submission refreshes in place; never reopen the editable conflict notice.
                      { guard: ({ event }) => event.output.type === "conflict", target: "refreshing" },
                      // Keep review and choices available for retry after an ordinary failure.
                      { target: "choosing", actions: assign({ writeFailed: true }) },
                    ],
                    onError: {
                      target: "choosing",
                      actions: { type: "failWrite", params: ({ event }) => ({ error: event.error }) },
                    },
                  },
                },
                // Retry refreshes the comparison while keeping the reconciliation page open.
                failed: { on: { SYNC: { target: "refreshing" } } },
              },
            },
          },
        },
        inactive: { type: "final" },
      },
    },
    // The author list, polled from `siblings` while the panel is expanded: fetch on entry, then wait before fetching
    // again, so requests never overlap. Requests can't be aborted, so a fetch outliving a collapse settles into idle.
    authors: {
      initial: "idle",
      states: {
        idle: { always: { guard: not("collapsed"), target: "fetching" } },
        fetching: {
          invoke: {
            src: "loadAuthors",
            onDone: {
              target: "settled",
              actions: assign({
                authors: ({ context, event }) => ({
                  status: "loaded",
                  index: indexScratchpadAuthors(event.output, context.ownerId),
                }),
              }),
            },
            onError: {
              // A failed refresh keeps the last loaded index.
              target: "settled",
              actions: assign(({ context, event }) => {
                console.error("Unable to load other scratchpads", event.error);
                return {
                  authors: context.authors.status === "loaded" ? context.authors : ({ status: "failed" } as const),
                };
              }),
            },
          },
        },
        settled: {
          always: { guard: "collapsed", target: "idle" },
          invoke: { src: "waitForPoll" },
          on: { "AUTHORS.DUE": { target: "fetching" } },
        },
        inactive: { type: "final" },
      },
    },
    // The scratchpad shown: the owner's, or another author's read-only, fetched by their listed reference.
    // Only a new selection or a failure clears what's shown; a newly listed version is fetched behind it.
    viewing: {
      initial: "own",
      states: {
        own: {
          entry: assign({ viewing: null }),
          on: {
            "AUTHOR.SELECT": {
              guard: ({ context, event }) => event.identityProviderId.toLowerCase() !== context.ownerId,
              target: "other",
              actions: assign({
                viewing: ({ event }) => ({
                  identityProviderId: event.identityProviderId.toLowerCase(),
                  snapshot: null,
                  fetched: null,
                }),
              }),
            },
          },
        },
        other: {
          initial: "ready",
          on: {
            "AUTHOR.SELECT": [
              {
                guard: ({ context, event }) => event.identityProviderId.toLowerCase() === context.ownerId,
                target: "own",
              },
              {
                guard: ({ context, event }) =>
                  event.identityProviderId.toLowerCase() === context.viewing?.identityProviderId,
              },
              // Reentering stops an in-flight fetch, so a late response can't show the previous author.
              {
                target: "other",
                reenter: true,
                actions: assign({
                  viewing: ({ event }) => ({
                    identityProviderId: event.identityProviderId.toLowerCase(),
                    snapshot: null,
                    fetched: null,
                  }),
                }),
              },
            ],
            // A capture adds to the owner's scratchpad, so show it. This handler takes precedence over the root's.
            CAPTURED: {
              target: "own",
              actions: assign({ latestAddedDataPointSource: ({ event }) => event.source }),
            },
          },
          states: {
            // A null snapshot has nothing to show: they haven't saved one, they cleared it, or the list hasn't loaded.
            ready: {
              always: [
                {
                  // Fetched before but no longer listed: they cleared their scratchpad.
                  guard: ({ context }) => context.viewing!.fetched != null && listedAuthor(context) == null,
                  actions: assign({
                    viewing: ({ context }) => ({ ...context.viewing!, snapshot: null, fetched: null }),
                  }),
                },
                {
                  // Compared with the listing, not the fetched snapshot, so a list and GET that disagree can't loop.
                  guard: ({ context }) => {
                    const listed = listedAuthor(context);
                    return listed != null && listed.concurrency !== context.viewing!.fetched;
                  },
                  target: "fetching",
                  actions: assign({
                    viewing: ({ context }) => ({ ...context.viewing!, fetched: listedAuthor(context)!.concurrency }),
                  }),
                },
              ],
            },
            // With a null snapshot this is the initial load; otherwise the current version stays shown meanwhile.
            fetching: {
              invoke: {
                src: "loadAuthor",
                input: ({ context }) => ({ reference: listedAuthor(context)!.reference }),
                onDone: {
                  target: "ready",
                  actions: assign({
                    viewing: ({ context, event }) => ({ ...context.viewing!, snapshot: event.output }),
                  }),
                },
                onError: {
                  target: "failed",
                  actions: [
                    assign({ viewing: ({ context }) => ({ ...context.viewing!, snapshot: null }) }),
                    ({ context, event }) =>
                      console.error(`Unable to load ${context.viewing!.identityProviderId}'s scratchpad`, event.error),
                  ],
                },
              },
            },
            // Polls don't retry; only the user does.
            failed: {
              on: {
                "AUTHOR.RETRY": {
                  target: "ready",
                  actions: assign({ viewing: ({ context }) => ({ ...context.viewing!, fetched: null }) }),
                },
              },
            },
          },
        },
        inactive: { type: "final" },
      },
    },
  },
});

export type ScratchpadActor = ActorRefFrom<typeof scratchpadMachine>;
export type ScratchpadMachineSnapshot = SnapshotFrom<typeof scratchpadMachine>;

export type ScratchpadStatus =
  | { kind: "loading" }
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "conflict"; message: string }
  | { kind: "failed"; message: string };

// The only source of scratchpad error text; apps render these rather than their own strings.
export const scratchpadMessages = {
  // Another author has no scratchpad: they haven't saved one, or cleared it. An empty one is never saved.
  empty: "Nothing added yet, or it's been cleared",
  // Another author's empty content or case summary; the other has something, or it wouldn't have been saved.
  nothingAdded: "Nothing added yet",
  // The owner's empty case summary.
  caseSummaryPlaceholder: "Add a case summary",
  loadFailed: "Scratchpad couldn't load",
  latestFailed: "Latest scratchpad couldn't load",
  changedElsewhere: "Scratchpad changed elsewhere",
  writeFailed: "Scratchpad request couldn't be processed",
  authorFailed: (identityProviderId: string) => `${identityProviderId}'s scratchpad couldn't load`,
} as const;

export function getScratchpadStatus(snapshot: ScratchpadMachineSnapshot): ScratchpadStatus {
  const { context } = snapshot;
  if (snapshot.matches({ owner: "loading" })) return { kind: "loading" };
  if (snapshot.matches({ owner: "loadFailed" })) return { kind: "failed", message: scratchpadMessages.loadFailed };
  if (context.conflict != null) {
    return {
      kind: "conflict",
      message: context.refreshError != null ? scratchpadMessages.latestFailed : scratchpadMessages.changedElsewhere,
    };
  }
  if (context.writeFailed) return { kind: "failed", message: scratchpadMessages.writeFailed };
  // A failed background refresh outside a conflict persists until the next successful refresh or save.
  if (context.refreshError != null) return { kind: "failed", message: scratchpadMessages.latestFailed };
  // Edits are pending until acknowledged, including while queued behind a background refresh.
  const dirty = context.document !== (context.acknowledged?.document ?? null);
  return snapshot.matches({ owner: "saving" }) || dirty ? { kind: "saving" } : { kind: "idle" };
}

export function hasScratchpadError(snapshot: ScratchpadMachineSnapshot): boolean {
  const { kind } = getScratchpadStatus(snapshot);
  return kind === "conflict" || kind === "failed";
}

// Statuses are rebuilt on every snapshot (each keystroke); compare them to keep the previous object.
export function isSameScratchpadStatus(a: ScratchpadStatus | undefined, b: ScratchpadStatus): boolean {
  return a?.kind === b.kind && ("message" in a ? a.message : null) === ("message" in b ? b.message : null);
}
